import { z } from 'zod';
import { isSafeRedirectUrl } from './safe-redirect';
import type { LoginReason, UnauthorizedReason } from './types';

/**
 * Navigator-page URL search params 의 검증 SSOT — zod schema 한 곳에서 담당.
 *
 * # 왜 schema layer
 * 함수 한 쌍 (`safeReturnTo` / `safeReason`) 형태에서 schema 로 전환한 이유: 진입
 * query 가 늘면서 (`token` 등) 검증 룰이 도메인별로 흩어지면 일관성이 깨지고, schema
 * 통일하면 새 query 가 schema field 한 줄 추가로 끝난다. 또 RR7 loader 가 페이지
 * 진입 *전에* 검증을 담당하므로 컴포넌트 본문이 검증 한 박자 늦지 않는다.
 *
 * # preprocess 전략
 * 모든 외부 입력 (`undefined`, 빈 string, 외부 URL, self-loop) 은 `preprocess` 가
 * normalize 후 typed 값으로만 흘려보낸다. 검증 실패는 throw 가 아니라 null/default
 * 로 fallback — UnauthorizedPage 가 사유 누락에 default 카피를 보여주는 식으로,
 * 진입 자체는 막지 않는다.
 *
 * # 검증 룰의 위협 모델
 * `redirectUrl` 이 외부 사이트로 redirect (open-redirect) / self-loop (`/login`,
 * `/error/*`) / 권한 escalation 진입점이 되는 시나리오를 차단. 자세한 분석은
 * `docs/adr/navigator-service.md` 의 위협 모델 섹션 (URL 입력) 참조.
 */

// ─── 위협 모델 상수 ──────────────────────────────────────────────────────────

/**
 * Self-loop 경로 prefix — auth funnel + 에러 페이지들. 두 가지 용도:
 *   1. `redirectUrl` 의 거부 대상 (사용자가 여기로 redirect 되는 흐름은 self-loop)
 *   2. composition.ts onAuthError 의 캡처 skip 조건 (현재 위치가 여기면 redirect 의미 없음)
 *
 * `/auth/` 는 인증 funnel 전체 (login / agreement / password / permission) 일괄 — funnel 은
 * 거쳐가는 단계지 redirect 목적지가 아니다. `/error/` 는 모든 에러 subpath 일괄.
 * 새 prefix 추가 시 본 배열 갱신.
 */
export const SELF_LOOP_PATH_PREFIXES: readonly string[] = ['/auth/', '/error/'] as const;

/** `UnauthorizedReason` union 의 runtime 화이트리스트 — TS union 과 1:1 정합. */
const UNAUTHORIZED_REASONS = [
  'link_expired',
  'session_expired',
  'unauthenticated',
] as const satisfies readonly UnauthorizedReason[];

/** `LoginReason` union 의 runtime 화이트리스트 — TS union 과 1:1 정합. */
const LOGIN_REASONS = [
  'session_expired',
  'subscription',
  'default',
] as const satisfies readonly LoginReason[];

// ─── 내부 검증 ──────────────────────────────────────────────────────────────

/**
 * pathname 이 self-loop 영역인지 검사 (segment 경계 매칭).
 * prefix `/login` 은 `/login` 자체 + `/login/foo` 차단, `/loginfoo` 는 통과.
 * prefix `/error/` 는 `/` 로 끝나서 그 아래 모든 path 일괄 차단.
 */
export function isSelfLoopPathname(pathname: string): boolean {
  for (const prefix of SELF_LOOP_PATH_PREFIXES) {
    const normalized = prefix.endsWith('/') ? prefix : prefix + '/';
    if (pathname === prefix || pathname.startsWith(normalized)) return true;
  }
  return false;
}

// ─── Schemas ─────────────────────────────────────────────────────────────────

/**
 * `redirectUrl` schema — 안전하지 않은 입력은 `null` 로 normalize (throw 없이 silent fallback).
 * 안전 판정 = `isSafeRedirectUrl` (현재 호스트 == 대상 호스트 또는 allowlist) → open-redirect 차단.
 * full URL 을 받는다(가드가 진입하려던 full URL 을 실어 보냄). self-loop 체크는 하지 않는다.
 *
 * URL 키 = `redirectUrl` (camelCase 일관성). TS 식별자도 `redirectUrl`.
 */
const redirectUrlField = z.preprocess(
  (v) => (isSafeRedirectUrl(typeof v === 'string' ? v : undefined) ? v : null),
  z.string().nullable(),
);

/**
 * `reason` schema — 화이트리스트 외 입력 (위변조, drift, 미정의 사유) 은
 * `'unauthenticated'` default 로 normalize.
 */
const reasonField = z.preprocess(
  (v) => (UNAUTHORIZED_REASONS.includes(v as UnauthorizedReason) ? v : 'unauthenticated'),
  z.enum(UNAUTHORIZED_REASONS),
);

/**
 * 로그인 `reason` schema — 화이트리스트 외 입력은 `'default'`(일반 로그인) 로 normalize.
 * `reasonField`(unauthorized)와 같은 `reason` URL 키를 공유하되 값 union 만 다름.
 */
const loginReasonField = z.preprocess(
  (v) => (LOGIN_REASONS.includes(v as LoginReason) ? v : 'default'),
  z.enum(LOGIN_REASONS),
);

/** `/login` 라우트의 검증된 search params. URL 키 = `redirectUrl`·`reason` (camelCase 일관성). */
export const loginSearchSchema = z.object({
  redirectUrl: redirectUrlField,
  reason: loginReasonField,
});

/**
 * `/auth/password`(임시비번 강제 변경) 라우트의 검증된 search params. 로그인 시 실려온
 * `redirectUrl` 을 그대로 이어받아 변경/스킵 후 복귀 목적지로 쓴다 (`reason` 은 불필요).
 */
export const passwordSearchSchema = z.object({
  redirectUrl: redirectUrlField,
});

/**
 * `/auth/consent`(약관 동의 온보딩) 라우트의 검증된 search params. 가드(`requireConsent`)가 진입하려던
 * 경로를 실어 보내므로, 동의 완료 후 원래 목적지로 복귀시킨다 (`passwordSearchSchema` 와 동일 계약).
 */
export const consentSearchSchema = z.object({
  redirectUrl: redirectUrlField,
});

/** `/error/unauthorized` 라우트의 검증된 search params. */
export const unauthorizedSearchSchema = z.object({
  reason: reasonField,
  redirectUrl: redirectUrlField,
});

export type LoginSearch = z.infer<typeof loginSearchSchema>;
export type PasswordSearch = z.infer<typeof passwordSearchSchema>;
export type ConsentSearch = z.infer<typeof consentSearchSchema>;
export type UnauthorizedSearch = z.infer<typeof unauthorizedSearchSchema>;

// ─── Loader helper ───────────────────────────────────────────────────────────

/**
 * RR7 loader 안에서 request URL 의 search params 를 schema 로 검증 후 typed 값을
 * 반환하는 factory. `composeLoaders` 와 결합해 가드 + 검증을 한 라우트의 loader 로
 * 합성한다 (RR7 의 라우트당 loader 1개 제약).
 */
export function parseSearchLoader<T>(schema: z.ZodType<T>) {
  return ({ request }: { request: Request }): T => {
    const url = new URL(request.url);
    return schema.parse(Object.fromEntries(url.searchParams));
  };
}
